/**
 * @voltxampere/engine — Assertion DSL unit arithmetic (Sprint 9)
 *
 * Shared helper module for the parser, evaluator, and message
 * formatter. Two responsibilities:
 *
 *   1. `unitMultiplier(unitString)` — convert a canonicalised
 *      unit token (`mV`, `kΩ`, `μA`) to the SI scaling factor
 *      `1e-3`, `1e3`, `1e-6`. Percent (`%`) is treated as a
 *      dimensionless `0.01` multiplier so the parser can use the
 *      same helper for `±1%` as for `±10mV`.
 *
 *   2. `formatEngineering(value, baseUnit)` — render a numeric
 *      value in engineering notation (3-digit mantissa, SI
 *      prefix). Used by the i18n failure messages so consumers
 *      see `1.23 kΩ` instead of `1230 Ω`. Matches ngspice's
 *      default print format.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Canonical SI prefix → multiplier. Kept in one place so the
 * lexer dictionary and the evaluator stay in sync. */
export const PREFIX_MULTIPLIER = new Map([
    ["G", 1e9],
    ["M", 1e6],
    ["k", 1e3],
    ["m", 1e-3],
    ["μ", 1e-6],
    ["n", 1e-9],
    ["p", 1e-12],
    ["f", 1e-15],
]);
/** Base units known to the DSL (must match `BASE_UNITS` in tokens.ts). */
export const BASE_UNIT_NAMES = ["V", "A", "Hz", "s", "W", "F", "H", "Ω"];
/**
 * Convert a canonical unit string to its SI multiplier.
 *
 * Examples:
 *   `"V"`  → 1
 *   `"mV"` → 1e-3
 *   `"kΩ"` → 1e3
 *   `"μA"` → 1e-6
 *   `"%"`  → 0.01
 *   `undefined` → 1 (dimensionless literal)
 */
export function unitMultiplier(unit) {
    if (unit === undefined || unit === "")
        return 1;
    if (unit === "%")
        return 0.01;
    if (BASE_UNIT_NAMES.includes(unit))
        return 1;
    // Try prefix + base split. Prefix is always the leading run of
    // non-base characters. We match longest-prefix-first.
    const prefixes = [...PREFIX_MULTIPLIER.keys()].sort((a, b) => b.length - a.length);
    for (const pfx of prefixes) {
        if (unit.startsWith(pfx)) {
            const rest = unit.slice(pfx.length);
            if (BASE_UNIT_NAMES.includes(rest)) {
                return PREFIX_MULTIPLIER.get(pfx) ?? 1;
            }
        }
    }
    // Unknown unit — treat as dimensionless to avoid crashing and
    // let the evaluator's dimensional-mismatch warning fire later.
    return 1;
}
/**
 * Extract the base portion (`V`, `Hz`, `Ω`, …) from a prefixed
 * unit. Returns the unit unchanged if it is already a base unit
 * or unrecognised. Used by `formatEngineering` so a value of
 * `1000` with unit `mV` is re-rendered as `1 V` (not `1 mmV`).
 */
export function unitBase(unit) {
    if (unit === undefined || unit === "")
        return "";
    if (unit === "%")
        return "%";
    if (BASE_UNIT_NAMES.includes(unit))
        return unit;
    const prefixes = [...PREFIX_MULTIPLIER.keys()].sort((a, b) => b.length - a.length);
    for (const pfx of prefixes) {
        if (unit.startsWith(pfx)) {
            const rest = unit.slice(pfx.length);
            if (BASE_UNIT_NAMES.includes(rest)) {
                return rest;
            }
        }
    }
    return unit;
}
/**
 * Engineering-notation render. Picks the SI prefix whose
 * multiplier is closest to (but not greater than) `|value|`, then
 * prints the mantissa at 3 significant digits.
 *
 * Special cases:
 *   - `value === 0`        → `"0 <baseUnit>"`
 *   - `|value| < 1e-15`    → use `f` (femto) prefix
 *   - `|value| ≥ 1e9`      → use `G` (giga) prefix, no capping
 *
 * Percent is passed through as a plain mantissa * 100 with `%`.
 */
/**
 * Sprint 23 G1 — Intl.NumberFormat locale-aware refactor.
 *
 * Mantissa Number(...toPrecision(3)) ile küçültülür, sonra
 * Intl.NumberFormat(locale)'e verilir → Türkçe locale'de
 * decimal "," + binlik ".", İngilizce locale'de tersine.
 *
 * `locale` parametresi opsiyonel — geçilmezse JavaScript'in
 * runtime default locale'i (genelde browser dil ayarı)
 * kullanılır. Engine code-path'ları çoğunlukla "en" geçer
 * (sentinel test'leri locale-bağımsız bekler); apps/web
 * UI çağırıcıları kullanıcının seçtiği locale'i geçer.
 */
export function formatEngineering(value, baseUnit, locale) {
    const unit = baseUnit ?? "";
    const formatNumber = (n) => {
        if (locale === undefined)
            return String(n);
        try {
            return new Intl.NumberFormat(locale, {
                useGrouping: false,
                maximumSignificantDigits: 3,
            }).format(n);
        }
        catch {
            // Unknown locale fallback → engine canonical String(n).
            return String(n);
        }
    };
    if (unit === "%") {
        const pct = value * 100;
        const display = locale === undefined ? pct.toPrecision(3) : formatNumber(pct);
        return `${display}${unit}`;
    }
    if (!Number.isFinite(value))
        return `${String(value)} ${unit}`.trim();
    if (value === 0) {
        const zero = locale === undefined ? "0" : formatNumber(0);
        return `${zero} ${unit}`.trim();
    }
    const mag = Math.abs(value);
    const tiers = [
        { mult: 1e9, pfx: "G" },
        { mult: 1e6, pfx: "M" },
        { mult: 1e3, pfx: "k" },
        { mult: 1, pfx: "" },
        { mult: 1e-3, pfx: "m" },
        { mult: 1e-6, pfx: "μ" },
        { mult: 1e-9, pfx: "n" },
        { mult: 1e-12, pfx: "p" },
        { mult: 1e-15, pfx: "f" },
    ];
    const fallback = tiers[tiers.length - 1] ?? { mult: 1, pfx: "" };
    let chosen = fallback;
    for (const tier of tiers) {
        if (mag >= tier.mult) {
            chosen = tier;
            break;
        }
    }
    const mantissa = value / chosen.mult;
    const formatted = Number(mantissa.toPrecision(3));
    const display = formatNumber(formatted);
    return `${display} ${chosen.pfx}${unit}`.trim();
}
//# sourceMappingURL=units.js.map